import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  VerifiedUser,
  LocalShipping,
  MonetizationOn,
  FeaturedPlayList,
} from "@mui/icons-material"; // Material-UI Icons
import { colors, fonts, size } from "../../ColorFont/color";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const AboutUs = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Box
      sx={{
        padding: 0,
        maxWidth: '1600px',
        margin: '0 auto',
      }}
    >

      <Box sx={{ textAlign: "center" }}>
        {/* Hero Section */}
        <Box
          sx={{
            color: colors.primary_dark,
            textAlign: "center",
            mb: { xs: 10, sm: 14 }, // Matched with Home page spacing
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontFamily: fonts.poppins,
              fontSize: { xs: 28, sm: 40 }, // Adjust font size for mobile
            }}
          >
            {t("aboutTitle1")}
          </Typography>
          <Typography
            sx={{
              fontFamily: fonts.poppins,
              fontSize: size.subtitle,
              mt: 1, // Add margin top for spacing
            }}
          >
            {t("aboutSubTitle")}
          </Typography>
        </Box>

        {/* Who We Are Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" }, // Stack vertically on mobile
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 4, sm: 10 }, // Adjust gap for mobile
            mb: { xs: 10, sm: 14 }, // Matched with Home page spacing
          }}
        >
          <Box sx={{ flex: 1, maxWidth: { xs: "100%", sm: "50%" } }}>
            <Typography
              sx={{
                color: colors.primary_dark,
                fontFamily: fonts.poppins,
                fontWeight: 600,
                fontSize: { xs: 24, sm: 30 }, // Adjust font size for mobile
                textAlign: { xs: "center", sm: "start" }, // Center text on mobile
              }}
            >
              {t("aboutTitle2")}
            </Typography>
            <Typography
              sx={{
                color: colors.text,
                mb: 2,
                fontFamily: fonts.poppins,
                fontSize: size.text,
                textAlign: { xs: "center", sm: "start" }, // Center text on mobile
              }}
            >
              {t("aboutDesc1")}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, maxWidth: { xs: "100%", sm: "50%" } }}>
            <img
              src="AU2.jpg" // Replace with your image URL
              alt="Who We Are"
              style={{
                width: "100%",
                borderRadius: "24px",
                boxShadow: "0px 12px 32px rgba(0,0,0,0.1)",
              }}
            />
          </Box>
        </Box>

        {/* What We Do Section */}
        <Box sx={{ mb: { xs: 10, sm: 14 } }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: colors.primary_dark,
              textAlign: "center",
              mb: 4,
              fontSize: { xs: 24, sm: 30 }, // Adjust font size for mobile
              fontFamily: fonts.poppins,
            }}
          >
            {t("aboutTitle2")}
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {[
              {
                icon: <FeaturedPlayList fontSize="large" />,
                title: t("whatPoint1"),
                description: t("aboutDesc2"),
              },
              {
                icon: <VerifiedUser fontSize="large" />,
                title: t("whatPoint2"),
                description: t("aboutDesc3"),
              },
              {
                icon: <LocalShipping fontSize="large" />,
                title: t("whatPoint3"),
                description: t("aboutDesc4"),
              },
              {
                icon: <MonetizationOn fontSize="large" />,
                title: t("whatPoint4"),
                description: t("aboutDesc5"),
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                    backgroundColor: colors.background3,
                    borderRadius: "24px",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: "0px 12px 32px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      textAlign: "center",
                    }}
                  >
                    <Box sx={{ color: colors.primary_dark, mb: 2 }}>
                      {item.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: colors.primary_dark,
                        mb: 2,
                        fontFamily: fonts.poppins,
                        fontSize: size.label,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.text,
                        fontSize: size.formLabel,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Why Choose KrushiSetu Section */}
        <Box sx={{ mb: { xs: 10, sm: 14 } }}>
          <Typography
            gutterBottom
            sx={{
              fontWeight: 600,
              color: colors.primary_dark,
              textAlign: "center",
              mb: 4,
              fontFamily: fonts.poppins,
              fontSize: { xs: 24, sm: 30 }, // Adjust font size for mobile
            }}
          >
            {t("aboutTitle4")}
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {[
              {
                icon: "🚀",
                title: t("whyPoint1"),
                description: t("aboutDesc6"),
              },
              {
                icon: "📱",
                title: t("whyPoint2"),
                description: t("aboutDesc7"),
              },
              {
                icon: "💰",
                title: t("whyPoint3"),
                description: t("aboutDesc8"),
              },
              {
                icon: "🌱",
                title: t("whyPoint4"),
                description: t("aboutDesc9"),
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                    backgroundColor: colors.background3,
                    borderRadius: "24px",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: "0px 12px 32px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="h4"
                      component="h3"
                      sx={{
                        mb: 2,
                        color: colors.primary_dark,
                      }}
                    >
                      {item.icon}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: colors.primary_dark,
                        mb: 2,
                        fontFamily: fonts.poppins,
                        fontSize: size.label,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.text,
                        fontFamily: fonts.poppins,
                        fontSize: size.formLabel,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Vision & Mission Section */}
        <Grid container spacing={4} sx={{ mb: { xs: 10, sm: 14 } }}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                p: 4,
                borderRadius: "24px",
                backgroundColor: "#FFFFFF",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
                border: `1px solid ${colors.primary_rgba_8 || 'rgba(0,0,0,0.05)'}`,
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0px 12px 30px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    backgroundColor: colors.primary_rgba_8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.primary_dark,
                  }}
                >
                  <Typography sx={{ fontSize: 24 }}>💡</Typography>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: colors.primary_dark,
                    fontFamily: fonts.poppins,
                    fontSize: { xs: 22, sm: 26 },
                  }}
                >
                  {t("vision")}
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: colors.text,
                  fontFamily: fonts.poppins,
                  fontSize: "15px",
                  lineHeight: 1.8,
                }}
              >
                {t("visionDesc")}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                p: 4,
                borderRadius: "24px",
                backgroundColor: "#FFFFFF",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
                border: `1px solid ${colors.primary_rgba_8 || 'rgba(0,0,0,0.05)'}`,
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0px 12px 30px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    backgroundColor: colors.primary_rgba_8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.primary_dark,
                  }}
                >
                  <Typography sx={{ fontSize: 24 }}>🎯</Typography>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: colors.primary_dark,
                    fontFamily: fonts.poppins,
                    fontSize: { xs: 22, sm: 26 },
                  }}
                >
                  {t("mission")}
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: colors.text,
                  fontFamily: fonts.poppins,
                  fontSize: "15px",
                  lineHeight: 1.8,
                }}
              >
                {t("missionDesc")}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Call to Action Section */}
        <Box
          sx={{
            color: colors.primary_dark,
            padding: 2,
            borderRadius: "24px",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontFamily: fonts.poppins,
              fontSize: { xs: 28, sm: 40 }, // Adjust font size for mobile
            }}
          >
            {t("join")}
          </Typography>
          <Typography
            sx={{
              mb: 4,
              fontFamily: fonts.poppins,
              color: colors.text,
              fontSize: size.text,
            }}
          >
            {t("joinDesc")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundImage: colors.button,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "50px", // Standard pill-shaped button
              textTransform: "none",
            }}
          >
            {t("getStarted")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AboutUs;
